import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Elements, RecurlyProvider } from '@recurly/react-recurly';
import {
  useHistory,
  useParams,
  Route,
  Switch,
  Redirect,
} from 'react-router-dom';
import { SimplePageLayout } from 'components/layouts/SimplePageLayout';
import { PageLayout } from 'components/PageLayout';
import { ENV } from 'shared/ENV';
import { useAuthState } from 'state/useAuthState';
import { useOrganizationState } from 'features/organizations/state/useOrganizationState';
import { useOrganizationDetails } from 'features/organizations/hooks/useOrganizationDetails';
import { OrganizationUpgradeSeatsForm } from 'features/organizations/components/OrganizationUpgradeSeatsForm';
import { OrganizationUpgradeBillingInfoForm } from 'features/organizations/components/OrganizationUpgradeBillingInfoForm';
import { OrganizationSuccessMessage } from 'features/organizations/components/OrganizationSuccessMessage';
import { Loader } from 'components/Loader';

type UpgradeFlowStep = 'seats' | 'billing' | 'success';

export const OrganizationUpgradeFlow = (): JSX.Element => {
  const { t } = useTranslation();
  const { step = 'seats' } = useParams<{ step?: UpgradeFlowStep }>();
  const history = useHistory();
  const { user } = useAuthState();
  const { details, isLoading: isLoadingOrgDetails } = useOrganizationDetails();

  const {
    updateOrganizationState,
    resetOrganizationState,
    ...organizationState
  } = useOrganizationState();

  const totalSeats =
    (organizationState.proSeats || 0) + (organizationState.regularSeats || 0);

  // In case the user visits this page directly to jump ahead, redirect them
  useEffect(() => {
    if (step === 'billing' && totalSeats === 0) {
      history.push('/organization/upgrade/');
    }
  }, [history, step, totalSeats]);

  const firstName = organizationState.firstName || user?.firstName;
  const lastName = organizationState.lastName || user?.lastName;
  const email = organizationState.email || user?.email;
  useEffect(() => {
    if (step === 'seats' && totalSeats === 0) {
      updateOrganizationState({
        firstName,
        lastName,
        email,
        proSeats: 0,
        regularSeats: 0,
      });
    }
  }, [updateOrganizationState, step, totalSeats, firstName, lastName, email]);

  const navigateToStep = (newStep: UpgradeFlowStep): void => {
    history.push(`/organization/upgrade/${newStep === 'seats' ? '' : newStep}`);
  };

  if (isLoadingOrgDetails) {
    return <Loader disableBackground />;
  }

  if (!details?.canPurchaseSeats || !details?.currentUserCanPurchaseSeats) {
    return <Redirect to="/organization" />;
  }

  const getHeaderAction = (): string => {
    if (step === 'seats') {
      return '/organization';
    }
    if (step === 'success') {
      return 'noControls';
    }
    return 'back';
  };

  return (
    <PageLayout
      data-cname="OrganizationUpgradeFlow"
      allowAppBanner={false}
      headerBarProps={{
        title: t('organization.manageMemberships'),
        action: getHeaderAction(),
      }}
    >
      <SimplePageLayout>
        <RecurlyProvider publicKey={ENV.VITE_RECURLY_PUBLIC_TOKEN}>
          <Elements>
            <Switch>
              <Route exact path="/organization/upgrade/">
                <OrganizationUpgradeSeatsForm
                  onSubmit={(data) => {
                    updateOrganizationState({
                      ...data,
                    });
                    navigateToStep('billing');
                  }}
                />
              </Route>
              <Route path="/organization/upgrade/billing/:new?">
                <OrganizationUpgradeBillingInfoForm
                  onSubmit={(data) => {
                    updateOrganizationState({
                      ...data,
                    });
                    navigateToStep('success');
                  }}
                />
              </Route>
              <Route path="/organization/upgrade/success">
                <OrganizationSuccessMessage
                  headingText={t('organizationPurchase.success.title')}
                  subheadingText={t('organizationPurchase.success.thanks')}
                  buttonText={t('organizationPurchase.success.button')}
                  to="/organization"
                />
              </Route>
            </Switch>
          </Elements>
        </RecurlyProvider>
      </SimplePageLayout>
    </PageLayout>
  );
};
